import {
  Component,
  OnDestroy,
  OnInit,
  signal,
  input,
  computed,
  ChangeDetectionStrategy,
  effect,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

/**
 * Reusable loading spinner component with rotating messages and progress bar.
 * Messages rotate every 2 seconds by default to keep users engaged
 * during long loading operations (like location indexing).
 * 
 * Features:
 * - Rotating messages to keep users engaged
 * - Progress bar that fills over maxTimeMs (default 10 seconds)
 * - When loading completes, progress bar quickly fills to 100%
 * - Two visual styles: 'simple' and 'overlay'
 * 
 * Usage:
 * <app-loading-spinner [phrases]="customPhrases" [intervalMs]="3000" />
 * <app-loading-spinner variant="overlay" [showDots]="true" [showProgress]="true" />
 */
@Component({
  selector: 'app-loading-spinner',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    @switch (variant()) {
      @case ('overlay') {
        <!-- Overlay Style (for modals/detail pages) -->
        <div
          class="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 max-w-sm mx-auto"
        >
          <div class="mb-6">
            <div
              class="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600 mx-auto"
            ></div>
          </div>
          <p
            class="text-lg font-medium text-gray-900 mb-2 transition-opacity duration-500"
          >
            {{ currentPhrase() }}
          </p>
          
          <!-- Progress Bar -->
          @if (showProgress()) {
            <div class="mt-4 w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
              <div
                class="h-full rounded-full transition-all"
                [class.bg-primary-500]="!isCompleting()"
                [class.bg-green-500]="isCompleting()"
                [style.width.%]="progress()"
                [style.transition-duration]="isCompleting() ? '300ms' : '100ms'"
              ></div>
            </div>
            <p class="text-xs text-gray-400 mt-2 text-center">
              {{ Math.round(progress()) }}%
            </p>
          }
          
          @if (showDots()) {
            <div class="flex justify-center gap-1 mt-4">
              @for (phrase of phrases(); track $index) {
                <div
                  class="w-2 h-2 rounded-full transition-all duration-300"
                  [class.bg-primary-600]="phraseIndex() === $index"
                  [class.bg-gray-300]="phraseIndex() !== $index"
                ></div>
              }
            </div>
          }
        </div>
      }
      @default {
        <!-- Simple Style (default) -->
        <div class="flex justify-center items-center py-12">
          <div class="text-center w-full max-w-xs mx-auto">
            <!-- Spinner -->
            <div
              class="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"
            ></div>
            
            <!-- Rotating Message -->
            <p class="text-pink-600 font-medium transition-opacity duration-300 mb-3">
              {{ currentPhrase() }}
            </p>
            
            <!-- Progress Bar -->
            @if (showProgress()) {
              <div class="w-full bg-pink-100 rounded-full h-1.5 overflow-hidden mx-auto">
                <div
                  class="h-full rounded-full transition-all"
                  [class.bg-pink-500]="!isCompleting()"
                  [class.bg-green-500]="isCompleting()"
                  [style.width.%]="progress()"
                  [style.transition-duration]="isCompleting() ? '300ms' : '100ms'"
                ></div>
              </div>
              <p class="text-xs text-pink-400 mt-2">
                {{ Math.round(progress()) }}%
              </p>
            }
            
            <!-- Progress Dots (optional) -->
            @if (showDots()) {
              <div class="flex justify-center gap-1 mt-3">
                @for (phrase of phrases(); track $index) {
                  <div
                    class="w-2 h-2 rounded-full transition-all duration-300"
                    [class.bg-pink-500]="phraseIndex() === $index"
                    [class.bg-pink-200]="phraseIndex() !== $index"
                  ></div>
                }
              </div>
            }
          </div>
        </div>
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoadingSpinnerComponent implements OnInit, OnDestroy {
  /** Custom loading phrases to display. Pass translation keys; they are resolved via TranslateService. */
  phrases = input<string[]>([
    'common.loadingPlaces',
    'loading.discoveringPlaces',
    'loading.personalizing',
    'loading.loadingBestOffers',
  ]);

  /** Interval in milliseconds between phrase changes. Defaults to 2000ms (2 seconds). */
  intervalMs = input<number>(2000);

  /** Whether to show progress dots below the message. Defaults to false. */
  showDots = input<boolean>(false);

  /** Whether to show the progress bar. Defaults to true. */
  showProgress = input<boolean>(true);

  /** Visual variant: 'simple' (default) or 'overlay' (card-style for modals). */
  variant = input<'simple' | 'overlay'>('simple');

  /** Maximum time in milliseconds for the progress bar. Defaults to 10000ms (10 seconds). */
  maxTimeMs = input<number>(10000);

  /** 
   * Signal to trigger completion animation.
   * When set to true, progress bar quickly fills to 100%.
   */
  complete = input<boolean>(false);

  /** Current phrase index */
  phraseIndex = signal(0);

  /** Current progress percentage (0-100) */
  progress = signal(0);

  /** Whether we're in the completion animation */
  isCompleting = signal(false);

  private translate = inject(TranslateService);

  /** Computed current phrase text (resolved through TranslateService) */
  currentPhrase = computed(() => {
    const phrasesArray = this.phrases();
    const index = this.phraseIndex();
    const key = phrasesArray[index] || phrasesArray[0] || 'common.loading';
    return this.translate.instant(key);
  });

  // Expose Math for template
  Math = Math;

  private intervalRef?: ReturnType<typeof setInterval>;
  private progressIntervalRef?: ReturnType<typeof setInterval>;
  private startTime: number = 0;

  constructor() {
    // Watch for complete input changes
    effect(() => {
      if (this.complete()) {
        this.completeProgress();
      }
    });
  }

  ngOnInit(): void {
    this.startRotation();
    this.startProgress();
  }

  ngOnDestroy(): void {
    this.stopRotation();
    this.stopProgress();
  }

  /**
   * Complete the progress bar quickly (call when data arrives).
   * This provides satisfying visual feedback that loading is done.
   */
  completeProgress(): void {
    this.isCompleting.set(true);
    this.stopProgress();
    
    // Quickly animate to 100%
    const currentProgress = this.progress();
    const remaining = 100 - currentProgress;
    const steps = 10;
    const stepSize = remaining / steps;
    let step = 0;

    const completeInterval = setInterval(() => {
      step++;
      this.progress.set(Math.min(100, currentProgress + stepSize * step));
      
      if (step >= steps) {
        clearInterval(completeInterval);
        this.progress.set(100);
      }
    }, 30); // Complete in ~300ms
  }

  private startRotation(): void {
    this.stopRotation();
    this.phraseIndex.set(0);

    this.intervalRef = setInterval(() => {
      const phrasesArray = this.phrases();
      if (phrasesArray.length > 1) {
        const currentIndex = this.phraseIndex();
        const nextIndex = (currentIndex + 1) % phrasesArray.length;
        this.phraseIndex.set(nextIndex);
      }
    }, this.intervalMs());
  }

  private stopRotation(): void {
    if (this.intervalRef) {
      clearInterval(this.intervalRef);
      this.intervalRef = undefined;
    }
  }

  private startProgress(): void {
    this.stopProgress();
    this.progress.set(0);
    this.isCompleting.set(false);
    this.startTime = Date.now();

    const maxTime = this.maxTimeMs();
    const updateIntervalMs = 100; // Update every 100ms

    this.progressIntervalRef = setInterval(() => {
      const elapsed = Date.now() - this.startTime;
      
      // Use easing function: starts slower, speeds up towards the end
      // But caps at 95% to leave room for completion animation
      const linearProgress = Math.min(elapsed / maxTime, 1);
      
      // Ease-out cubic: slower at start, faster towards end
      // This creates anticipation and makes the wait feel shorter
      const easedProgress = 1 - Math.pow(1 - linearProgress, 2);
      
      // Cap at 95% so there's always room for the completion animation
      const cappedProgress = Math.min(easedProgress * 100, 95);
      
      this.progress.set(cappedProgress);

      // Stop when we reach max time (but stay at 95%)
      if (elapsed >= maxTime) {
        this.stopProgress();
      }
    }, updateIntervalMs);
  }

  private stopProgress(): void {
    if (this.progressIntervalRef) {
      clearInterval(this.progressIntervalRef);
      this.progressIntervalRef = undefined;
    }
  }
}
